import React, { useState, useEffect } from 'react';
import { AppShell } from '../components/Layout';
import { navigatorAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/Icon';

const ROLES = ['HEALTH_NAVIGATOR', 'ADMIN', 'DOCTOR', 'SUPPORT_STAFF'];

const ROLE_LABELS = {
  ADMIN:            { label: 'Admin',            color: 'badge-purple' },
  HEALTH_NAVIGATOR: { label: 'Health Navigator', color: 'badge-confirmed' },
  DOCTOR:           { label: 'Doctor',           color: 'badge-active' },
  SUPPORT_STAFF:    { label: 'Support Staff',    color: 'badge-pending' },
};

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '',
  role: 'HEALTH_NAVIGATOR', phone: '',
  // Whether patients may start a chat with this person. Defaults to true
  // because the default role is HEALTH_NAVIGATOR, for whom it is true anyway;
  // switching the role below re-derives it (see setRole).
  patientMessageable: true,
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'ADMIN';

  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [showPwForm, setShowPwForm] = useState(null); // userId
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [search,     setSearch]     = useState('');

  const load = () => {
    setLoading(true);
    navigatorAPI.list()
      .then(r => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set  = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setError(''); setSuccess('');
    setShowForm(true);
    setTimeout(() => document.getElementById('user-form')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({
      firstName: u.firstName, lastName: u.lastName, email: u.email,
      role: u.role, phone: u.phone || '',
      // The API returns the EFFECTIVE value (role-derived when unset), so the
      // checkbox always reflects what the patient picker actually does today.
      patientMessageable: u.patientMessageable !== false,
    });
    setError(''); setSuccess('');
    setShowForm(true);
    setTimeout(() => document.getElementById('user-form')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleCancel = () => {
    setShowForm(false); setEditId(null);
    setForm(EMPTY_FORM); setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess(''); setTempPassword('');
    try {
      if (editId) {
        await navigatorAPI.update(editId, {
          firstName: form.firstName,
          lastName:  form.lastName,
          role:      form.role,
          phone:     form.phone || '',
          // Always sent explicitly — the backend leaves this untouched when the
          // field is absent, precisely so the self-service profile save (which
          // never sends it) cannot reset it.
          patientMessageable: form.patientMessageable,
        });
        setSuccess('User updated successfully!');
      } else {
        const res = await navigatorAPI.create(form);
        // The server generates the password now — the admin never chooses it.
        // Shown once here because WhatsApp delivery is best-effort: no phone on
        // the account, or a WATI failure, leaves this as the only route to the
        // new user.
        setTempPassword(res.data?.temporaryPassword || '');
        setSuccess(res.data?.temporaryPassword
          ? 'User created. Share the temporary password below — it is shown only once.'
          : 'User created successfully!');
      }
      setShowForm(false); setEditId(null);
      setForm(EMPTY_FORM); load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save user.');
    } finally { setSaving(false); }
  };

  const handleToggle = async (u) => {
    try {
      await navigatorAPI.toggleActive(u.id);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, isActive: !x.isActive } : x));
    } catch { setError('Failed to update user status.'); }
  };

  // The admin no longer picks the replacement password — the server generates
  // one, WhatsApps it to the user and returns it here so it can be relayed if
  // that send did not land. Same rule as account creation: a credential its
  // owner did not choose is flagged for rotation at their next login.
  const handleResetPassword = async e => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess(''); setTempPassword('');
    try {
      const res = await navigatorAPI.changePassword(showPwForm, {});
      setSuccess('Password reset. The user must change it at their next login.');
      setTempPassword(res.data?.temporaryPassword || '');
      setShowPwForm(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally { setSaving(false); }
  };

  const filtered = users.filter(u =>
    !search ||
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.role.toLowerCase().includes(search.toLowerCase())
  );

  const initials = u => `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`.toUpperCase();

  if (!isAdmin) {
    return (
      <AppShell breadcrumbs={[{ label: 'Users' }]}>
        <div className="empty-state" style={{ paddingTop: 80 }}>
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="lock" size={20} /></div>
          <div className="empty-title">Admin Access Required</div>
          <div className="empty-desc">You need admin privileges to manage users.</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell breadcrumbs={[{ label: 'Users' }]}>
      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">User Management</div>
          <div className="page-subtitle">Create and manage navigator accounts</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={openAdd}>+ Add User</button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row fade-in-up delay-1" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <div className="stat-card"><div className="stat-label">Total Users</div><div className="stat-value">{users.length}</div></div>
        <div className="stat-card"><div className="stat-label">Active</div><div className="stat-value">{users.filter(u => u.isActive).length}</div></div>
        <div className="stat-card"><div className="stat-label">Navigators</div><div className="stat-value">{users.filter(u => u.role === 'HEALTH_NAVIGATOR').length}</div></div>
        <div className="stat-card"><div className="stat-label">Admins</div><div className="stat-value">{users.filter(u => u.role === 'ADMIN').length}</div></div>
      </div>

      {error   && <div className="alert alert-error"><Icon name="alert" size={14} /> {error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}
      {tempPassword && (
        <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Temporary password</div>
            <code style={{ fontSize: 15, letterSpacing: '0.5px' }}>{tempPassword}</code>
            <div style={{ fontSize: 12, marginTop: 6 }}>
              Also sent on WhatsApp if the user has a number. It cannot be retrieved
              again — reset the password to issue a new one.
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm"
            onClick={() => navigator.clipboard?.writeText(tempPassword)}>
            Copy
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar fade-in-up delay-2">
        <div className="search-input-wrap">
          <span className="search-icon"><Icon name="search" size={15} /></span>
          <input className="search-input" placeholder="Search by name, email, role…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Users table */}
      <div className="table-wrap fade-in-up delay-3">
        <table>
          <thead>
            <tr>
              <th>User</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={6}><div className="loading-state"><div className="spinner"/>Loading…</div></td></tr>
              : filtered.length === 0
                ? <tr><td colSpan={6}><div className="empty-state"><div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="person" size={20} /></div><div className="empty-title">No users found</div></div></td></tr>
                : filtered.map(u => (
                  <tr key={u.id} style={{ opacity: u.isActive ? 1 : 0.55 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                          background: u.isActive
                            ? 'linear-gradient(135deg, var(--brand-dark), var(--brand-accent))'
                            : 'var(--border)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 13, fontWeight: 700, color: 'var(--text-on-brand-dark)',
                        }}>{initials(u)}</div>
                        <div>
                          <div style={{ fontWeight: 700 }}>{u.firstName} {u.lastName}</div>
                          {u.id === currentUser?.id && (
                            <div style={{ fontSize: 11, color: 'var(--brand-accent)', fontWeight: 600 }}>You</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>{u.email}</td>
                    <td>
                      <span className={`badge ${ROLE_LABELS[u.role]?.color || 'badge-pending'}`}>
                        {ROLE_LABELS[u.role]?.label || u.role}
                      </span>
                      {/* Only worth showing where it differs from what the role
                          implies — an admin who takes patient chats, or a
                          navigator who does not. Flagging every navigator would
                          be noise. */}
                      {u.role !== 'DOCTOR' && u.patientMessageable !== (u.role === 'HEALTH_NAVIGATOR') && (
                        <span
                          className={`badge ${u.patientMessageable ? 'badge-confirmed' : 'badge-pending'}`}
                          style={{ marginLeft: 6, fontSize: 10 }}
                          title={u.patientMessageable
                            ? 'Patients can start a chat with this user'
                            : 'Hidden from the patient’s “Message a navigator” list'}
                        >
                          {u.patientMessageable ? 'Patient chat' : 'No patient chat'}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${u.isActive ? 'badge-active' : 'badge-inactive'}`}>
                        {u.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>✏ Edit</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setShowPwForm(u.id); setError(''); }}>Reset Password</button>
                        {u.id !== currentUser?.id && (
                          <button
                            className={`btn btn-sm ${u.isActive ? 'btn-danger' : 'btn-ghost'}`}
                            style={!u.isActive ? { color: 'var(--green)', borderColor: 'var(--green)' } : {}}
                            onClick={() => handleToggle(u)}>
                            {u.isActive ? '✕ Disable' : '✓ Enable'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
        <div className="table-footer">
          <span>Showing {filtered.length} of {users.length} users</span>
        </div>
      </div>

      {/* Reset Password modal */}
      {showPwForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(13,30,53,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}
          onClick={e => { if (e.target === e.currentTarget) setShowPwForm(null); }}>
          <div style={{
            background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)',
            padding: 32, width: 420, boxShadow: 'var(--shadow-xl)',
          }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
              Reset Password
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
              {users.find(u => u.id === showPwForm)?.firstName} {users.find(u => u.id === showPwForm)?.lastName}
            </div>
            {error && <div className="alert alert-error" style={{ marginBottom: 16 }}><Icon name="alert" size={14} /> {error}</div>}
            <form onSubmit={handleResetPassword}>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
                A new one-time password will be generated and sent to this user on
                WhatsApp. It is shown to you once afterwards in case that message
                does not arrive. Their current password stops working immediately,
                and they must set a new one at their next login.
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? 'Resetting…' : 'Reset Password'}
                </button>
                <button className="btn btn-ghost" type="button" onClick={() => { setShowPwForm(null); setError(''); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add / Edit Form */}
      {showForm && (
        <div className="form-section fade-in-up" id="user-form" style={{ marginTop: 28 }}>
          <div className="form-section-title">
            {editId ? 'Edit User' : 'Add New User'}
          </div>
          {/* Adding a user means typing SOMEONE ELSE's details, so browser
              autofill only ever offered the admin's own name and email here.
              Edit mode keeps the browser's default behaviour. */}
          <form onSubmit={handleSubmit} autoComplete={editId ? undefined : 'off'}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">First Name <span className="required">*</span></label>
                <input className="form-input" value={form.firstName} onChange={set('firstName')}
                  placeholder="First name" required
                  name={editId ? undefined : 'new-user-first-name'}
                  autoComplete={editId ? undefined : 'off'} />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name <span className="required">*</span></label>
                <input className="form-input" value={form.lastName} onChange={set('lastName')}
                  placeholder="Last name" required
                  name={editId ? undefined : 'new-user-last-name'}
                  autoComplete={editId ? undefined : 'off'} />
              </div>
              <div className="form-group">
                <label className="form-label">Email <span className="required">*</span></label>
                <input className="form-input" type="email" value={form.email} onChange={set('email')}
                  placeholder="user@previu.com" required disabled={!!editId}
                  name={editId ? undefined : 'new-user-email'}
                  autoComplete={editId ? undefined : 'off'}
                  style={editId ? { background: 'var(--bg-subtle)', color: 'var(--text-muted)' } : {}} />
              </div>
              <div className="form-group">
                <label className="form-label">Role <span className="required">*</span></label>
                <select
                  className="form-select"
                  value={form.role}
                  onChange={(e) => {
                    // Re-derive the patient-chat default from the new role, so
                    // switching to Admin/Support doesn't silently leave someone
                    // messageable just because the previous role was Navigator.
                    // Still overridable below — that is the whole point of it
                    // being a separate capability rather than part of the role.
                    const role = e.target.value;
                    setForm(f => ({ ...f, role, patientMessageable: role === 'HEALTH_NAVIGATOR' }));
                  }}
                  required
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{ROLE_LABELS[r]?.label || r}</option>
                  ))}
                </select>
              </div>
              {/* Patient-chat capability — deliberately separate from Role.
                  navigators.role is single-valued, so an admin who also carries
                  patients had no way to be offered in the patient's "Message a
                  navigator" picker without giving up admin rights. Doctors are
                  excluded server-side regardless of this box; they are reached
                  through the dedicated doctor picker. */}
              <div className="form-group">
                <label className="form-label">Patient Chat</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  fontSize: 13, color: 'var(--text-primary)', padding: '9px 0' }}>
                  <input
                    type="checkbox"
                    checked={!!form.patientMessageable}
                    onChange={(e) => setForm(f => ({ ...f, patientMessageable: e.target.checked }))}
                    disabled={form.role === 'DOCTOR'}
                  />
                  Patients can start a chat with this user
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  {form.role === 'DOCTOR'
                    ? 'Doctors are messaged through the patient’s Care Team, not the navigator list.'
                    : 'Controls whether patients and subscribers can pick this user when they message a navigator. Team Chat is not affected: staff and doctors can message this user there either way.'}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">WhatsApp Number</label>
                <input className="form-input" type="tel" value={form.phone} onChange={set('phone')}
                  placeholder="e.g. 9876543210"
                  name={editId ? undefined : 'new-user-whatsapp'}
                  autoComplete={editId ? 'tel' : 'off'} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  Used for WhatsApp OTP login
                </div>
              </div>
              {/* No password field: the server generates a one-time password so
                  the admin never chooses — or knows — another person's
                  credential, the same way doctor logins are provisioned. It is
                  WhatsApp'd to the user and shown once here as a fallback. */}
              {!editId && (
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0',
                  fontSize: 13, color: 'var(--text-muted)', gap: 6 }}>
                  ℹ️ A one-time password is generated and sent to the user on WhatsApp.
                  They must change it at first login.
                </div>
              )}
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : editId ? '✓ Update User' : '✓ Create User'}
              </button>
              <button className="btn btn-ghost" type="button" onClick={handleCancel}>Cancel</button>
            </div>
          </form>
        </div>
      )}
    </AppShell>
  );
}
